import { supabase } from './supabase';

// Wrapper fino sobre a edge function enviar-cobranca-email — mesmo papel de
// gatewayResolver.ts para o Asaas. Nunca lança: todo erro (rede, timeout,
// resposta não-2xx) vira { ok: false }, porque o chamador (BoletoEmitidoModal)
// não pode deixar uma falha de e-mail cair no catch do envio principal, que
// já foi commitado (fatura em "Cobrança enviada", WhatsApp já disparado).

// Um único formato com campos opcionais, em vez de union discriminado por
// `ok` — este projeto não roda com "strict": true no tsconfig, e sem
// strictNullChecks o TS não estreita (narrow) union types em cadeias
// if/else if (confirmado: o mesmo código com "strict" ligado compila limpo).
// Optional em vez de union evita depender desse narrowing.
export type ResultadoEmailCobranca = {
  ok: boolean;
  destinatarios?: string[];
  anexos?: string[];
  warnings?: string[];
  motivo?: 'opt_out' | 'uc_nao_encontrada' | 'sem_destinatario' | 'ja_enviado_recentemente' | 'erro';
  mensagem?: string;
};

const INVOKE_TIMEOUT_MS = 30000;

/** 'cobranca' (padrão) leva valor, vencimento, PIX, linha digitável e o boleto.
 *  'comunicado' é a fatura que pulou o cálculo: só o aviso de que a fatura do
 *  mês está disponível, com o PDF da distribuidora em anexo. */
export type ModoEmailCobranca = 'cobranca' | 'comunicado';

export async function enviarCobrancaEmail(
  faturaId: string | number,
  opts?: { destinatarios?: string[]; forcar?: boolean; modo?: ModoEmailCobranca },
): Promise<ResultadoEmailCobranca> {
  try {
    const invoke = supabase.functions.invoke('enviar-cobranca-email', {
      body: {
        fatura_id: faturaId,
        destinatarios: opts?.destinatarios,
        forcar: opts?.forcar,
        modo: opts?.modo,
      },
    });

    // supabase-js não aceita AbortSignal aqui — a corrida contra o timer é a
    // única forma de não pendurar o botão. Se estourar, a function continua
    // rodando no servidor e ainda grava o resultado em faturas; o banco é a
    // fonte da verdade, isto aqui é só cortesia para a UI não travar.
    const { data, error } = await Promise.race([
      invoke,
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), INVOKE_TIMEOUT_MS)
      ),
    ]);

    if (error) {
      const detalhe = await (error as any)?.context?.clone?.().json?.().catch(() => null);
      const mensagem = detalhe?.message || detalhe?.error || (error as any)?.message || 'Falha ao enviar o e-mail da cobrança.';
      return { ok: false, motivo: 'erro', mensagem };
    }

    if (data?.error) {
      return { ok: false, motivo: 'erro', mensagem: data.message || data.error };
    }

    if (data?.success === false) {
      const motivosConhecidos = ['opt_out', 'uc_nao_encontrada', 'sem_destinatario', 'ja_enviado_recentemente'] as const;
      const motivo = motivosConhecidos.find(m => m === data.reason) || 'erro';
      return {
        ok: false,
        motivo,
        mensagem: data.message || 'Não foi possível enviar o e-mail da cobrança.',
      };
    }

    return {
      ok: true,
      destinatarios: data?.destinatarios || [],
      anexos: data?.anexos || [],
      warnings: data?.warnings || [],
    };
  } catch (e: any) {
    if (e?.message === 'timeout') {
      return {
        ok: false,
        motivo: 'erro',
        mensagem: 'O envio do e-mail está demorando mais que o normal e continua em processamento. Confira o resultado no card "Cobrança Enviada".',
      };
    }
    return { ok: false, motivo: 'erro', mensagem: e?.message || 'Falha ao enviar o e-mail da cobrança.' };
  }
}
